/**
 * The article's companion video, wired to the tracker.
 *
 * A thin adapter on purpose. `VideoEmbed` lives in `@elyxeer/ui` and knows nothing
 * about analytics — it emits a typed progress event and this module decides what
 * that means. That is what keeps the design system free of app imports and lets the
 * admin panel reuse the very same player as an editor preview.
 */

import {
  VideoEmbed,
  type VideoPosition,
  type VideoProgressEvent,
} from "@elyxeer/ui";
import { useTranslation } from "react-i18next";

import { track } from "~/lib/analytics";
import { AnalyticsEventName } from "~/types/analytics";

interface Props {
  youtubeId: string;
  title: string;
  subjectId: string;
  subjectKey: string;
  contentFormat: string;
  /** Where the member stopped last time; the player opens there. */
  startSeconds?: number;
  /** Where the member is now, for "Pokračovat". */
  onPosition?: (position: VideoPosition) => void;
  /** The video played to its end. */
  onComplete?: () => void;
}

export function ArticleVideo({
  youtubeId,
  title,
  subjectId,
  subjectKey,
  contentFormat,
  startSeconds,
  onPosition,
  onComplete,
}: Props) {
  const { t } = useTranslation();

  const onProgress = (event: VideoProgressEvent) => {
    const subject = {
      subjectType: "news" as const,
      subjectId,
      subjectKey,
      contentFormat,
    };
    if (event.kind === "start") {
      track(AnalyticsEventName.VideoStart, subject);
      return;
    }
    track(AnalyticsEventName.VideoProgress, {
      ...subject,
      value: event.percent ?? null,
      // Carried so a scrub to the end stays distinguishable from a real watch.
      props: { quartiles_watched: event.quartilesWatched },
    });
    if (event.kind === "complete") onComplete?.();
  };

  return (
    <VideoEmbed
      youtubeId={youtubeId}
      title={t("article.video.title", { title })}
      unavailableLabel={t("article.video.unavailable")}
      onProgress={onProgress}
      onPosition={onPosition}
      startSeconds={startSeconds}
    />
  );
}
